import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-13019" data-mp="1gxo0eyldcbv6">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-13020" data-mp="1gxxouhqrmayr">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-13021" data-mp="16yp32c6202nh">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-13022" data-mp="1qt9ii1dyz8fn">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-13023" data-mp="1c4546vaapoct">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-13024" data-mp="1h0ve10vibnps">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-13025" data-mp="24tkd9fieh763">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-13026" data-mp="1vr2r112qwej3">
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/*
              NOTE (antwoord-lek fix): the previous "stem + -te/-de" result badge
              was REMOVED from stimulus mode. As a stimulus it pre-computed the
              full kofschip decision AND revealed the intact loanword stem-spelling
              — i.e. it handed the learner the exact answer the MC/fill-in item is
              meant to test (e.g. seed stem:"race", useTE:true → showed "race + -te"
              → option "racete"). The KOFSCHIP letter grid + "niet in 't kofschip"
              indicator stay: they present the *mnemonic tool* (and, for klank-vs-
              letter traps like 'race', the deliberate misconception) without
              resolving the answer. The full worked result still lives in SUPPORT
              mode below, shown via the rule/support aid, not in the stimulus.
            */}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13027" data-mp="446nrj3xcesb">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-13028" data-mp="42vjzjahb717">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13029" data-mp="dtylunb8fq1j">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-13030" data-mp="dswzhmornh4i">
            <span className="text-2xl" data-id="element-13031" data-mp="3dbg3ydyggzfl">⛵</span>
            <div className="text-left" data-id="element-13032" data-mp="3ha6qxs97j0oz">
              <div className="text-sm font-bold text-blue-900" data-id="element-13033" data-mp="3hb8g10n25npp">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-13034" data-mp="3r94p7mwfq8l6">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Ezelsbruggetje (Tip) */}
        <div className="mb-6 bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13035" data-mp="16inedxy82n1o">
          <div className="flex items-start gap-3" data-id="element-13036" data-mp="2ffkh3ei98cu7">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13037" data-mp="2fgdpsl0rxcez">
              <span className="text-amber-600 text-lg" data-id="element-13038" data-mp="2ailrn99jcu2c">💡</span>
            </div>
            <div data-id="element-13039" data-mp="2oi3idu6e6hec">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13040" data-mp="2oefnaqlatmxi">
                Ezelsbruggetje
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13041" data-mp="2yeq22fmzf2es">
                Zit de laatste letter van de stam in{' '}
                <strong data-id="element-13042" data-mp="2tgesm60lmtx3">'t KOFSCHIP</strong>?
                <br data-id="element-13043" data-mp="2tfd6n6i1on0l" />✅ Ja → <strong data-id="element-13044" data-mp="2tdirk8tgfp4f">-te(n)</strong>
                <br data-id="element-13045" data-mp="377jn6tp40w0k" />❌ Nee → <strong data-id="element-13046" data-mp="375yocrvriqww">-de(n)</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Stap-voor-stap voorbeeld (Compact) */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-13047" data-mp="337so82pfj9ky">
          <div className="flex items-start gap-3" data-id="element-13048" data-mp="3h2v0d7kvdv67">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13049" data-mp="3c75wwt5qbu4m">
              <span className="text-emerald-600 text-lg" data-id="element-13050" data-mp="3vsfx0vj5aszz">📝</span>
            </div>
            <div className="w-full" data-id="element-13051" data-mp="4kk8ftq8n9ka">
              <h4 className="text-sm font-bold text-emerald-900 mb-4" data-id="element-13052" data-mp="4jitvfa0k4xt">
                Voorbeeld: {verb}
              </h4>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-13053" data-mp="3qsjrusypoviu">
                <div className="text-center" data-id="element-13054" data-mp="3d0n5l5jq5fud">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13055" data-mp="3hs565shu5eet">Stam</div>
                  <div className="font-bold text-lg text-blue-700" data-id="element-13056" data-mp="3htgb3vn0os7i">{stem}</div>
                </div>
                <div className="text-slate-400" data-id="element-13057" data-mp="33524d9mhcjlo">→</div>
                <div className="text-center" data-id="element-13058" data-mp="381ix6h4mjm53">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13059" data-mp="3807s8dzg08ce">
                    Laatste letter
                  </div>
                  <div className={`font-bold text-xl ${isInKofschip ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-13060" data-mp="2y44oj3oy01nc">
                    {lastLetter.toUpperCase()}
                  </div>
                </div>
                <div className="text-slate-400" data-id="element-13061" data-mp="ztx7srj0f9hv">→</div>
                <div className="text-center" data-id="element-13062" data-mp="uvm0ang6tb2t">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13063" data-mp="uwdw9zdp5atm">
                    In 't KOFSCHIP?
                  </div>
                  <div className="font-bold text-lg" data-id="element-13064" data-mp="pyc8x298mpcc">
                    {isInKofschip ? '✅ Ja' : '❌ Nee'}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center p-3 rounded-lg border-2 font-bold bg-white shadow-sm" data-id="element-13065" data-mp="1imhyc2b8l2ii">
                Dus: <span className="text-emerald-700" data-id="element-13066" data-mp="1dkis8ko43rhv">{stem}</span> +{' '}
                <span className="text-emerald-700" data-id="element-13067" data-mp="19l9yhft2ntmb">
                  {useTE ? 'te(n)' : 'de(n)'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Steps */}
        <details className="group bg-white/60 rounded-xl border border-blue-100 shadow-sm overflow-hidden" data-id="element-13068" data-mp="3u966odj5wv70">
          <summary className="cursor-pointer p-4 font-semibold text-blue-800 hover:bg-blue-50/50 transition-colors flex items-center justify-between" data-id="element-13069" data-mp="29ujg9hvafniz">
            <span data-id="element-13070" data-mp="2isv4jd8om989">Meer uitleg (stap voor stap)</span>
            <span className="text-blue-400 group-open:rotate-180 transition-transform" data-id="element-13071" data-mp="2iraadoqa4sax">
              ▼
            </span>
          </summary>
          <div className="p-5 border-t border-blue-100 space-y-6" data-id="element-13072" data-mp="2sjptipdbbmxe">
            {/* Step 1: Verb → Stem */}
            <div className="flex items-start gap-3" data-id="element-13073" data-mp="2xhhwcefhwtgd">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13074" data-mp="2xkdqdro82xzj">
                <span className="text-blue-600 font-bold text-xs" data-id="element-13075" data-mp="36izavsd98n2y">1</span>
              </div>
              <div data-id="element-13076" data-mp="175v51hi5zjs9">
                <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-13077" data-mp="176d7wwzc0w95">
                  Vind de stam
                </h4>
                <p className="text-sm text-slate-600" data-id="element-13078" data-mp="1c5zpfim70ynr">
                  Haal -en van het hele werkwoord af: <strong data-id="element-13079" data-mp="3e1f6w1saqy0w">{verb}</strong> →{' '}
                  <strong data-id="element-13080" data-mp="3e043w46oduau">{stem}</strong>
                </p>
              </div>
            </div>

            {/* Step 2: Last Letter Check */}
            <div className="flex items-start gap-3" data-id="element-13081" data-mp="3nsjnf5xmeqxu">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13082" data-mp="3nueb2s0a0x5m">
                <span className="text-cyan-600 font-bold text-xs" data-id="element-13083" data-mp="2v3coi5u97zc2">2</span>
              </div>
              <div data-id="element-13084" data-mp="2r20wxurtl418">
                <h4 className="text-sm font-bold text-cyan-900 mb-1" data-id="element-13085" data-mp="2r2iy95tca8g0">
                  Kijk naar de laatste letter
                </h4>
                <p className="text-sm text-slate-600" data-id="element-13086" data-mp="353da5yflirjf">
                  De laatste letter van de stam is de{' '}
                  <strong data-id="element-13087" data-mp="304sac049a9vi">{lastLetter.toUpperCase()}</strong>.
                </p>
              </div>
            </div>

            {/* Step 3: KOFSCHIP Check */}
            <div className="flex items-start gap-3" data-id="element-13088" data-mp="fbf2cp20s3om">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13089" data-mp="feug9ojyj6og">
                <span className="text-indigo-600 font-bold text-xs" data-id="element-13090" data-mp="229pn7holjcir">3</span>
              </div>
              <div className="w-full" data-id="element-13091" data-mp="1o9oni5k2ib4v">
                <h4 className="text-sm font-bold text-indigo-900 mb-3" data-id="element-13092" data-mp="1oaq8b1qvybzs">
                  Zit deze in 't KOFSCHIP?
                </h4>

                <div className="flex justify-start gap-1.5 mb-3 flex-wrap" data-id="element-13093" data-mp="1tacn3fmd40ap">
                  <div className="text-lg font-black text-slate-400 mr-1" data-id="element-13094" data-mp="2gyvvdv5ewd2s">
                    't
                  </div>
                  {kofschipLetters.map(letter => {
                  const isMatch = letter === lastLetter.toUpperCase();
                  return <div key={letter} className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm shadow-sm
                          ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-13095" data-mp="2c62q966172q6">
                        {letter}
                      </div>;
                })}
                </div>

                {!isInKofschip && <div className="text-sm text-rose-600 font-semibold mb-2" data-id="element-13096" data-mp="bfujnovm71x7">
                    De {lastLetter.toUpperCase()} zit er NIET in.
                  </div>}
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>;
}